import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './DashboardSaasPage.css';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface CrecheResumo {
  id: number;
  nome: string;
  cidade: string;
  estado: string;
  email?: string;
  status: 'pendente' | 'ativa' | 'suspensa' | 'negada';
  criado_em?: string;
}


interface UserResumo {
  id: number;
  criado_em?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_META: Record<
  CrecheResumo['status'],
  { label: string; classe: string; icon: string }
> = {
  pendente:  { label: 'Pendente',  classe: 'badge-pendente',  icon: '⏳' },
  ativa:     { label: 'Ativa',     classe: 'badge-ativa',     icon: '✅' },
  suspensa:  { label: 'Suspensa',  classe: 'badge-suspensa',  icon: '⚠️' },
  negada:    { label: 'Negada',    classe: 'badge-negada',    icon: '❌' },
};

function fmtData(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function DashboardSaasPage() {
  const { usuario } = useAuth();

  const [creches, setCreches] = useState<CrecheResumo[]>([]);
  const [users, setUsers] = useState<UserResumo[]>([]);
  const [logsHoje, setLogsHoje] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [acaoId, setAcaoId] = useState<number | null>(null);
  const [motivoNegacao, setMotivoNegacao] = useState('');
  const [dialogId, setDialogId] = useState<number | null>(null);

  // ── Carrega dados ──────────────────────────────────────────────────────────

  const carregarDados = useCallback(async () => {
    setCarregando(true);
    try {
      const hojeISO = new Date().toISOString().slice(0, 10);
      const [rCreches, rUsers, rLogs] = await Promise.allSettled([
        api.get<CrecheResumo[]>('/creches'),
        api.get<UserResumo[]>('/saas/usuarios'),
        api.get('/lgpd/auditoria', { params: { de: hojeISO, limit: 1 } }),
      ]);
      setCreches(rCreches.status === 'fulfilled' ? (rCreches.value.data ?? []) : []);
      setUsers(rUsers.status === 'fulfilled' ? (rUsers.value.data ?? []) : []);
      // A auditoria é paginada ({ data, total }): usa o total do dia para o KPI.
      setLogsHoje(rLogs.status === 'fulfilled' ? Number((rLogs.value.data as any)?.total ?? 0) : 0);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  // ── Ação de status ─────────────────────────────────────────────────────────

  async function atualizarStatus(id: number, status: CrecheResumo['status'], motivo?: string) {
    setAcaoId(id);
    try {
      await api.patch(`/creches/${id}/status`, { status, motivo_negacao: motivo ?? null });
      await carregarDados();
    } finally {
      setAcaoId(null);
      setDialogId(null);
      setMotivoNegacao('');
    }
  }

  // Aprovar um lead provisiona o tenant (cria/ativa o usuário-gestor e inicia o
  // trial de 7 dias). Creches cadastradas manualmente (sem assinatura) caem no
  // fallback de apenas marcar como ativa.
  async function aprovarSolicitacao(id: number) {
    setAcaoId(id);
    try {
      const resp = await api.post(`/saas/provisionar-creche/${id}`);
      const data: any = resp?.data ?? {};
      // O e-mail de aviso ao gestor engole falhas de SMTP silenciosamente no
      // backend. Se não saiu, o admin precisa avisar o gestor manualmente —
      // caso contrário o interessado fica sem orientação de acesso.
      if (data.emailEnviado === false) {
        alert(
          data.aviso ||
            `Creche aprovada e acesso liberado, mas o e-mail de aviso não pôde ser enviado. ` +
              `Avise o gestor manualmente que ele já pode entrar em ${data.loginUrl || '/login'} ` +
              `com o e-mail e a senha cadastrados.`,
        );
      }
      await carregarDados();
    } catch (err: any) {
      if (err?.response?.status === 404) {
        await atualizarStatus(id, 'ativa');
        return;
      }
      alert(err?.response?.data?.message || 'Erro ao aprovar solicitação.');
    } finally {
      setAcaoId(null);
    }
  }

  // ── KPIs ───────────────────────────────────────────────────────────────────

  const pendentes  = creches.filter(c => c.status === 'pendente').length;
  const ativas     = creches.filter(c => c.status === 'ativa').length;
  const suspensas  = creches.filter(c => c.status === 'suspensa').length;
  const negadas    = creches.filter(c => c.status === 'negada').length;

  // Usuários cadastrados nos últimos 30 dias
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const newcomers = users.filter(u => u.criado_em && new Date(u.criado_em) >= thirtyDaysAgo).length;

  // logsHoje vem do total paginado da auditoria (estado carregado em carregarDados)

  const kpis = [
    { key: 'total',      icon: '🏫', label: 'Total de Creches',        valor: String(creches.length),   cor: 'kpi-azul'     },
    { key: 'pendentes',  icon: '⏳', label: 'Solicitações Pendentes',   valor: String(pendentes),        cor: pendentes > 0 ? 'kpi-alerta' : 'kpi-cinza' },
    { key: 'ativas',     icon: '✅', label: 'Creches Ativas',          valor: String(ativas),           cor: 'kpi-verde'    },
    { key: 'suspensas',  icon: '⚠️', label: 'Suspensas',               valor: String(suspensas),        cor: suspensas > 0 ? 'kpi-alerta' : 'kpi-cinza' },
    { key: 'negadas',    icon: '❌', label: 'Negadas',                  valor: String(negadas),          cor: 'kpi-cinza'    },
    { key: 'usuarios',   icon: '👥', label: 'Usuários na Plataforma',  valor: String(users.length),     cor: 'kpi-azul'     },
    { key: 'newcomers',  icon: '📈', label: 'Novos Usuários (30d)',     valor: String(newcomers),        cor: 'kpi-verde'    },
    { key: 'logs',       icon: '🔒', label: 'Logs de Segurança Hoje',  valor: String(logsHoje),         cor: 'kpi-roxo'     },
  ];

  const solicitacoesPendentes = creches.filter(c => c.status === 'pendente');
  const outrasCreches = creches.filter(c => c.status !== 'pendente');

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="saas-dashboard">
      {/* Cabeçalho */}
      <div className="saas-header">
        <div>
          <h2 className="pagina-titulo">Dashboard — Administração SaaS</h2>
          <p className="pagina-subtitulo">
            Bem-vindo, <strong>{usuario?.email}</strong>! Você está no painel de controle global da plataforma.
          </p>
        </div>
        <button
          className="btn-atualizar"
          onClick={carregarDados}
          disabled={carregando}
          title="Recarregar dados"
        >
          {carregando ? '⟳ Atualizando…' : '⟳ Atualizar'}
        </button>
      </div>

      {/* Spinner */}
      {carregando && <div className="saas-spinner">Carregando dados da plataforma…</div>}

      {/* KPI Cards */}
      {!carregando && (
        <div className="saas-kpi-grid">
          {kpis.map(k => (
            <div key={k.key} className={`saas-kpi-card ${k.cor}`}>
              <span className="saas-kpi-icon">{k.icon}</span>
              <div>
                <p className="saas-kpi-label">{k.label}</p>
                <p className="saas-kpi-valor">{k.valor}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Tabela de Solicitações Pendentes ── */}
      {!carregando && (
        <section className="saas-section">
          <h3 className="saas-section-title">
            ⏳ Solicitações de Ingresso Pendentes
            {pendentes > 0 && <span className="saas-badge-count">{pendentes}</span>}
          </h3>

          {solicitacoesPendentes.length === 0 ? (
            <div className="saas-empty">Nenhuma solicitação pendente. ✅</div>
          ) : (
            <div className="saas-table-wrapper">
              <table className="saas-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nome</th>
                    <th>Cidade / UF</th>
                    <th>E-mail</th>
                    <th>Cadastro</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {solicitacoesPendentes.map(c => (
                    <tr key={c.id}>
                      <td>#{c.id}</td>
                      <td>{c.nome}</td>
                      <td>{c.cidade} / {c.estado}</td>
                      <td>{c.email ?? '—'}</td>
                      <td>{fmtData(c.criado_em)}</td>
                      <td className="saas-acoes">
                        <button
                          className="btn-aprovar"
                          disabled={acaoId === c.id}
                          onClick={() => aprovarSolicitacao(c.id)}
                          title="Aprovar ingresso e iniciar trial de 7 dias"
                        >
                          ✅ Aprovar
                        </button>
                        <button
                          className="btn-negar"
                          disabled={acaoId === c.id}
                          onClick={() => setDialogId(c.id)}
                          title="Negar ingresso"
                        >
                          ❌ Negar
                        </button>
                        <button
                          className="btn-suspender"
                          disabled={acaoId === c.id}
                          onClick={() => atualizarStatus(c.id, 'suspensa')}
                          title="Suspender"
                        >
                          ⚠️ Suspender
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ── Tabela de Todas as Creches ── */}
      {!carregando && outrasCreches.length > 0 && (
        <section className="saas-section">
          <h3 className="saas-section-title">🏫 Creches Cadastradas na Plataforma</h3>
          <div className="saas-table-wrapper">
            <table className="saas-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Cidade / UF</th>
                  <th>Status</th>
                  <th>Cadastro</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {outrasCreches.map(c => {
                  const meta = STATUS_META[c.status];
                  return (
                    <tr key={c.id}>
                      <td>#{c.id}</td>
                      <td>{c.nome}</td>
                      <td>{c.cidade} / {c.estado}</td>
                      <td>
                        <span className={`saas-badge ${meta.classe}`}>
                          {meta.icon} {meta.label}
                        </span>
                      </td>
                      <td>{fmtData(c.criado_em)}</td>
                      <td className="saas-acoes">
                        {c.status !== 'ativa' && (
                          <button className="btn-aprovar" disabled={acaoId === c.id}
                            onClick={() => atualizarStatus(c.id, 'ativa')}>
                            ✅ Ativar
                          </button>
                        )}
                        {c.status !== 'suspensa' && (
                          <button className="btn-suspender" disabled={acaoId === c.id}
                            onClick={() => atualizarStatus(c.id, 'suspensa')}>
                            ⚠️ Suspender
                          </button>
                        )}
                        {c.status !== 'negada' && (
                          <button className="btn-negar" disabled={acaoId === c.id}
                            onClick={() => setDialogId(c.id)}>
                            ❌ Negar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Dialog de Negação ── */}
      {dialogId !== null && (
        <div className="saas-overlay" onClick={() => { setDialogId(null); setMotivoNegacao(''); }}>
          <div className="saas-dialog" onClick={e => e.stopPropagation()}>
            <h4>❌ Negar Solicitação</h4>
            <p>Informe o motivo da negação (opcional, mas recomendado):</p>
            <textarea
              className="saas-textarea"
              rows={4}
              placeholder="Descreva o motivo da negação…"
              value={motivoNegacao}
              onChange={e => setMotivoNegacao(e.target.value)}
            />
            <div className="saas-dialog-acoes">
              <button className="btn-negar" onClick={() => atualizarStatus(dialogId, 'negada', motivoNegacao)}>
                Confirmar Negação
              </button>
              <button className="btn-cancelar" onClick={() => { setDialogId(null); setMotivoNegacao(''); }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
